/** @format */
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { FormikProps } from "formik";
import * as React from "react";
import { useAppSelector } from "../../app/hooks";
import { selectConstants } from "./Api/constantSlice";
import { ErrorBox } from "../custom/ErrorBox";
import { APPLICATION_STAGE } from "../models/enums/applicationStage";
import { getApplicationStageName } from "../Logic/commonLogic";


interface Props {
  formik: FormikProps<any>;
  isDisabled: boolean;
  currentApplicationStatus: BackendApplicationStage;
}
export type BackendApplicationStage = Exclude<
  APPLICATION_STAGE,
  APPLICATION_STAGE.All | APPLICATION_STAGE.Assessed | APPLICATION_STAGE.NotAssessed
>;

export default function ApplicationStatus({
  formik,
  isDisabled,
  currentApplicationStatus,
}: Props) {
  const selData = useAppSelector(selectConstants);

  const sendApplicationBacklistData = () => {
    const records = selData.constantState.applicationStages;
    if (!records) return [];

    return records
      .filter(
        (s) =>
          s === APPLICATION_STAGE.PersonalDetails ||
          s === APPLICATION_STAGE.AddressDetails ||
          s === APPLICATION_STAGE.StudyDetails ||
          s === APPLICATION_STAGE.InstitutionDetails ||
          s === APPLICATION_STAGE.DocumentsUpload ||
          s === APPLICATION_STAGE.LegalGuardianDetails
      )
      .map((item) => (
        <MenuItem key={item} value={item}>
          <ArrowBackIcon sx={{ color: "red", mr: 1, fontSize: 15 }} />
          {getApplicationStageName(item)}
        </MenuItem>
      ));
  };

  const completeApplicationStatusData = () => {
    const records = selData.constantState.applicationStages;
    if (!records) return [];
  
    let results = records.filter(
      (s): s is BackendApplicationStage =>
        s !== APPLICATION_STAGE.PersonalDetails &&
        s !== APPLICATION_STAGE.AddressDetails &&
        s !== APPLICATION_STAGE.StudyDetails &&
        s !== APPLICATION_STAGE.InstitutionDetails &&
        s !== APPLICATION_STAGE.DocumentsUpload &&
        s !== APPLICATION_STAGE.IncompleteApplication &&
        s !== APPLICATION_STAGE.ApplicationSubmitted &&
        s !== APPLICATION_STAGE.LegalGuardianDetails &&
        s !== APPLICATION_STAGE.Assessed &&
        s !== APPLICATION_STAGE.NotAssessed
    );
  
    // Additional filtering for specific current status
    if (
      [
        APPLICATION_STAGE.ApplicationInReview,
        APPLICATION_STAGE.ApplicationDeclined,
        APPLICATION_STAGE.ApplicationSentForFurtherReview,
        APPLICATION_STAGE.ApplicationFullyAssessedAndIsPendingUniversitySelection,
        APPLICATION_STAGE.ApplicationSuccessful,
        APPLICATION_STAGE.ApplicationSubmitted,
      ].includes(currentApplicationStatus)
    ) {
      results = results.filter(
        (s) => s !== APPLICATION_STAGE.ApplicationSubmitted
      );
    }
  
    return results.map((item) => (
      <MenuItem key={item} value={item} sx={{ display: "flex" }}>
        <ArrowForwardIcon sx={{ color: "green", mr: 1, fontSize: 15 }} />
        {getApplicationStageName(item)}
      </MenuItem>
    ));
  };

  return (
    <>
      {selData.constantState.applicationStages && (
        <>
          <FormControl fullWidth>
            <InputLabel
              error={
                formik.touched.applicationStages &&
                Boolean(formik.errors.applicationStages)
              }
              id="application-status-select"
            >
              Update Status
            </InputLabel>
            <Select
              name="applicationStages"
              labelId="application-status-select"
              id="application-status-select"
              value={formik.values.applicationStages}
              label="Application Stages"
              disabled={isDisabled}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                formik.setFieldValue("applicationStages", e.target.value);
                formik.setFieldValue("institutionId", 0);
                formik.setFieldValue("selectedIds", []);
              }}
              error={
                formik.touched.applicationStages &&
                Boolean(formik.errors.applicationStages)
              }
            >
              <MenuItem value={-1} disabled>
                Update Status
              </MenuItem>
              <MenuItem disabled></MenuItem>
              <MenuItem disabled>Complete Application</MenuItem>
              {completeApplicationStatusData()}
              <MenuItem disabled>Incomplete Application (Send back)</MenuItem>
              {sendApplicationBacklistData()}
            </Select>
          </FormControl>

          <ErrorBox>
            {formik.touched.applicationStages &&
              (formik.errors.applicationStages as React.ReactNode)}
          </ErrorBox>
        </>
      )}
    </>
  );
}
